import { UAParser } from "ua-parser-js";

import { GAME_TITLE } from "./constants";
import { puzzleIndex } from "./time-utils";
import { generateEmojiGrid } from "./game-helpers";

// Share logic from here: https://github.com/cwackerfuss/react-wordle/blob/main/src/lib/share.ts
const webShareApiDeviceTypes = ["mobile", "smarttv", "wearable"];
const parser = new UAParser();
const browser = parser.getBrowser();
const device = parser.getDevice();

/**
 * Check if native Web Share API is available
 */
export const canNativeShare = () => {
    return (
        browser.name?.toUpperCase().indexOf("FIREFOX") === -1 &&
        webShareApiDeviceTypes.indexOf(device.type ?? "") !== -1 &&
        navigator.canShare &&
        navigator.share
    );
};

/**
 * Share text using native share or clipboard fallback
 */
export const shareGame = async (text) => {
    const shareData = { text };

    if (canNativeShare() && navigator.canShare(shareData)) {
        try {
            await navigator.share(shareData);
            return true;
        } catch (error) {
            // User cancelled or share failed, fall through to clipboard
            if (error.name !== 'AbortError') {
                console.error('Share failed:', error);
            }
        }
    }

    // Fallback to clipboard
    if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        return true;
    }

    throw new Error('Sharing not supported');
};

/**
 * Legacy share status function for backwards compatibility
 */
export const shareStatus = (
    gameData,
    submittedGuesses,
    handleShareToClipboard,
    handleShareFailure,
    includeGameLink = true
) => {
    const GAME_URL = window.location.href;
    const textToShare =
        `${GAME_TITLE} #${puzzleIndex}\n\n` +
        generateEmojiGrid(gameData, submittedGuesses, true) +
        `${includeGameLink ? "\n\n" + GAME_URL : ""}`;

    const shareData = { text: textToShare };

    let shareSuccess = false;

    try {
        if (attemptShare(shareData)) {
            navigator.share(shareData);
            shareSuccess = true;
        }
    } catch (error) {
        shareSuccess = false;
    }

    try {
        if (!shareSuccess) {
            if (navigator.clipboard) {
                navigator.clipboard
                    .writeText(textToShare)
                    .then(handleShareToClipboard)
                    .catch(handleShareFailure);
            } else {
                handleShareFailure();
            }
        }
    } catch (error) {
        handleShareFailure();
    }
};

const attemptShare = (shareData) => {
    return (
        // Deliberately exclude Firefox Mobile, because its Web Share API isn't working correctly
        browser.name?.toUpperCase().indexOf("FIREFOX") === -1 &&
        webShareApiDeviceTypes.indexOf(device.type ?? "") !== -1 &&
        navigator.canShare &&
        navigator.canShare(shareData) &&
        navigator.share
    );
};
